import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

function formatMoney(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatUptime(minutes) {
  const m = Number(minutes || 0);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 48) return `${h}h ${rem}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function guildIconUrl(guild) {
  if (!guild?.id || !guild?.icon) return null;
  const ext = String(guild.icon).startsWith("a_") ? "gif" : "png";
  return `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.${ext}?size=64`;
}

function StatCard({ label, value, hint }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
      {hint ? <div className="admin-stat-hint">{hint}</div> : null}
    </div>
  );
}

function Section({ title, children, actions }) {
  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export default function AdminPage({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const token = localStorage.getItem("kami_token");

  const load = useCallback(async () => {
    if (!token) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data: overview } = await axios.get(
        `${API_BASE}/admin/overview`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(overview);
      setForbidden(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setForbidden(true);
        setData(null);
      } else {
        setError(
          err.response?.data?.error || "Could not load admin overview.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user || forbidden) {
    return (
      <div className="admin-page">
        <h1>Admin</h1>
        <p className="admin-lead">
          This page is reserved for the bot owner.{" "}
          <Link to="/">Return home</Link>
        </p>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="admin-page">
        <div className="page-loading">
          <div className="sync-spinner" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="admin-page">
        <h1>Admin</h1>
        <p className="admin-error">{error}</p>
        <button type="button" className="btn btn-secondary" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const botReady = data?.bot?.status === "Ready";
  const dbConnected = data?.database?.state === "Connected";

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <p className="admin-eyebrow">Owner only</p>
          <h1>Kami admin</h1>
          <p className="admin-lead">
            Live Discord membership, Mongo leftovers, donors, and mod activity.
            {data?.generatedAt
              ? ` Updated ${new Date(data.generatedAt).toLocaleString()}.`
              : ""}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div className="admin-stat-grid">
        <StatCard
          label="Bot"
          value={data.bot.status}
          hint={
            botReady
              ? `Uptime ${formatUptime(data.bot.uptimeMinutes)}${
                  data.bot.version ? ` · v${data.bot.version}` : ""
                }`
              : "Heartbeat stale or offline"
          }
        />
        <StatCard
          label="Database"
          value={data.database.state}
          hint={
            dbConnected
              ? `${data.database.players.toLocaleString()} players`
              : null
          }
        />
        <StatCard
          label="Live servers"
          value={data.bot.guildsLive}
          hint={
            data.bot.guildsHeartbeat != null &&
            data.bot.guildsHeartbeat !== data.bot.guildsLive
              ? `Heartbeat last wrote ${data.bot.guildsHeartbeat}`
              : "From Discord (unique IDs)"
          }
        />
        <StatCard
          label="Mongo guild docs"
          value={data.database.guildDocs}
          hint={
            data.database.orphanGuildDocs
              ? `${data.database.orphanGuildDocs} left / stale`
              : "All docs match live guilds"
          }
        />
      </div>

      <div className="admin-columns">
        <Section title="Supporters">
          <div className="admin-mini-grid">
            <StatCard label="Supporter" value={data.donors.supporter} />
            <StatCard label="Patron" value={data.donors.patron} />
            <StatCard label="Benefactor" value={data.donors.benefactor} />
            <StatCard
              label="Lifetime flags"
              value={data.donors.lifetimeSupporter}
            />
            <StatCard label="Active subs" value={data.donors.activeSubs} />
            <StatCard
              label="Donation total"
              value={formatMoney(data.donors.donationTotalCents)}
            />
          </div>
        </Section>

        <Section title="Mod actions">
          <div className="admin-mod-tables">
            {[
              ["Last 7 days", data.modActions.last7d],
              ["Last 30 days", data.modActions.last30d],
            ].map(([label, row]) => (
              <div key={label} className="admin-mod-block">
                <h3>{label}</h3>
                <ul>
                  <li>
                    <span>Total</span>
                    <strong>{row.total}</strong>
                  </li>
                  {["ban", "kick", "warn", "timeout", "jail", "unban"].map(
                    (key) => (
                      <li key={key}>
                        <span>{key}</span>
                        <strong>{row[key] || 0}</strong>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section title="Feature adoption (Mongo)">
        <div className="admin-mini-grid">
          {Object.entries(data.features).map(([key, value]) => (
            <StatCard key={key} label={key} value={value} />
          ))}
        </div>
      </Section>

      <Section title={`Servers Kami is in (${data.servers.length})`}>
        {!data.servers.length ? (
          <p className="admin-empty">No live guilds returned from Discord.</p>
        ) : (
          <ul className="admin-server-list">
            {data.servers.map((guild) => {
              const icon = guildIconUrl(guild);
              return (
                <li key={guild.id}>
                  {icon ? (
                    <img src={icon} alt="" className="admin-server-icon" />
                  ) : (
                    <span className="admin-server-icon fallback" aria-hidden />
                  )}
                  <div className="admin-server-meta">
                    <span className="admin-server-name">{guild.name}</span>
                    <span className="admin-server-id">{guild.id}</span>
                  </div>
                  <div className="admin-server-flags">
                    {!guild.inMongo ? (
                      <span className="admin-flag warn">no mongo</span>
                    ) : null}
                    {guild.features?.welcome ? (
                      <span className="admin-flag">welcome</span>
                    ) : null}
                    {guild.features?.tickets ? (
                      <span className="admin-flag">tickets</span>
                    ) : null}
                    {guild.features?.jail ? (
                      <span className="admin-flag">jail</span>
                    ) : null}
                    {guild.features?.bumpRemind ? (
                      <span className="admin-flag">bump</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {data.orphanServers?.length ? (
        <Section
          title={`Stale Mongo guilds (${data.database.orphanGuildDocs})`}
        >
          <p className="admin-lead" style={{ marginBottom: 12 }}>
            Documents for servers Kami is no longer in (first 50 shown).
          </p>
          <ul className="admin-server-list compact">
            {data.orphanServers.map((guild) => (
              <li key={guild.id}>
                <span className="admin-server-icon fallback" aria-hidden />
                <div className="admin-server-meta">
                  <span className="admin-server-name">{guild.name}</span>
                  <span className="admin-server-id">{guild.id}</span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
